import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  StoreLocationModel,
  StoreMenuModel,
  StoreGatewayModel,
  StoreModel,
  StoreCategoryModel,
  StoreProductModel,
  StoreCouponModel,
  LoyaltyLevelModel,
  OrderTypeModel,
} from "../models";
import { GetStoreLocations, GetStoreMenu } from "../services";
import { getStoreIdFromUrl, getUtmFromUrl } from "../helpers";
import { OrderType } from "../enums";
import { RootState } from ".";
import { Toast } from "../components/Toast";

const utmTag = import.meta.env.VITE_APP_UTM_TAG;

// ---- Initial values (similar to your context) ----
const initialStoreId = getStoreIdFromUrl();
const utmFromUrl = getUtmFromUrl();

const initialUtm = (() => {
  try {
    const stored = localStorage.getItem("olo_utm") || "";
    if (utmFromUrl) {
      // prefer UTM from URL and store it
      localStorage.setItem("olo_utm", utmFromUrl);
      // remove fragment from URL
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
      return utmFromUrl;
    }
    return stored;
  } catch {
    return utmFromUrl || "";
  }
})();

export interface StoreState {
  storeId: string;
  menuId: string;
  utm: string;
  isMenuLoading: boolean;
  isLocationLoading: boolean;
  giftcardMenu?: StoreMenuModel;
  storeMenu?: StoreMenuModel;
  storeCategories: StoreCategoryModel[];
  storeProducts: StoreProductModel[];
  storeCoupons: StoreCouponModel[];
  storeLocations?: StoreLocationModel;
  selectedStore?: StoreModel;
  storeGateways: StoreGatewayModel[];
  isLoyaltyActive: boolean;
  loyaltyLevels: LoyaltyLevelModel[];
  isGiftcardActive: boolean;
}

const initialState: StoreState = {
  storeId: initialStoreId,
  menuId: "",
  utm: initialUtm,
  isMenuLoading: false,
  isLocationLoading: false,
  giftcardMenu: undefined,
  storeMenu: undefined,
  storeCategories: [],
  storeProducts: [],
  storeCoupons: [],
  storeLocations: undefined,
  selectedStore: undefined,
  storeGateways: [],
  isLoyaltyActive: false,
  loyaltyLevels: [],
  isGiftcardActive: false,
};

// ---- Thunks (menu + locations) ----
export const fetchStoreMenu = createAsyncThunk<
  StoreMenuModel,
  string,
  { state: RootState; rejectValue: string }
>(
  "store/fetchMenu",
  async (storeId, { rejectWithValue }) => {
    const { status, message, data } = await GetStoreMenu({ store_id: storeId });

    if (!status) {
      Toast.error(message);
      return rejectWithValue(message);
    }

    return data;
  },
  {
    condition: (_, { getState }) => {
      const { store } = getState();

      // already loading or already loaded → skip
      if (store.isMenuLoading) return false;
      if (store.storeMenu) return false;

      return true;
    },
  },
);

export const fetchStoreLocations = createAsyncThunk<
  { data: StoreLocationModel; storeId: string },
  string,
  { state: RootState; rejectValue: string }
>(
  "store/fetchLocations",
  async (storeId, { rejectWithValue }) => {
    const { status, message, data } = await GetStoreLocations({
      store_id: storeId,
    });

    if (!status) {
      Toast.error(message);
      return rejectWithValue(message);
    }

    if (data?.name) {
      document.title = data.name;
    }

    const root = document.documentElement;

    const hexToRgb = (hex: string): string => {
      // Remove the '#' if present
      hex = hex.replace(/^#/, "");

      // Convert shorthand (#FFF) to full form (#FFFFFF)
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((char) => char + char)
          .join("");
      }

      // Validate hex code
      if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        throw new Error("Invalid hex color code");
      }

      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      return `${r}, ${g}, ${b}`;
    };

    root.style.setProperty(
      "--brand-primary",
      data.theme_color.replace("##", "#"),
    );
    root.style.setProperty(
      "--brand-secondary",
      data.theme_color_secondary.replace("##", "#"),
    );
    root.style.setProperty(
      "--brand-primary-soft-rgb",
      hexToRgb(data.theme_color.replace("##", "#")),
    );

    if (data?.version === "3.0") {
      window.location.href = `https://ordering.app2food.com/order3/${storeId}`;
      return rejectWithValue("Redirecting");
    }

    return { data, storeId };
  },
  {
    condition: (_, { getState }) => {
      const { store } = getState();

      if (store.isLocationLoading) return false;
      if (store.storeLocations) return false;

      return true;
    },
  },
);

// ---- Slice ----
const storeSlice = createSlice({
  name: "store",
  initialState,
  reducers: {
    setStoreId(state, action: PayloadAction<string>) {
      state.storeId = action.payload;
    },
    setMenuId(state, action: PayloadAction<string>) {
      state.menuId = action.payload;
    },
    setUtm(state, action: PayloadAction<string>) {
      state.utm = action.payload;
      try {
        localStorage.setItem("olo_utm", action.payload);
      } catch {}
    },
    setStoreGateways(state, action: PayloadAction<StoreGatewayModel[]>) {
      state.storeGateways = action.payload;
    },
    setGiftcardMenu(state, action: PayloadAction<StoreMenuModel>) {
      state.giftcardMenu = action.payload;
    },
    applyInflation(state, action: PayloadAction<string | number>) {
      const typeId = action.payload;

      state.storeProducts = state.storeProducts.map((product) => {
        if (!product.is_inflated) return product;

        const productInflation = Array.isArray(product.convenience)
          ? 0
          : Number(product.convenience[String(typeId)] || 0);

        const updatedModifierGroups = product.modifier_group.map(
          (modifierGroup) => ({
            ...modifierGroup,
            modifier: modifierGroup.modifier.map((modifier) => {
              const modifierInflation = Array.isArray(modifier.convenience)
                ? 0
                : Number(modifier.convenience?.[String(typeId)] || 0);

              return {
                ...modifier,
                m_price: modifier.m_mprice + modifierInflation,
              };
            }),
          }),
        );

        return {
          ...product,
          product_price: product.product_mrice + productInflation,
          modifier_group: updatedModifierGroups,
        };
      });
    },
  },
  extraReducers: (builder) => {
    // Menu
    builder
      .addCase(fetchStoreMenu.pending, (state) => {
        state.isMenuLoading = true;
      })
      .addCase(fetchStoreMenu.fulfilled, (state, action) => {
        state.isMenuLoading = false;
        state.storeMenu = action.payload;
        state.menuId = action.payload.menu_id;
        state.storeCategories = action.payload.category;
        state.storeProducts = action.payload.product;
        state.storeCoupons = action.payload.coupons;
      })
      .addCase(fetchStoreMenu.rejected, (state) => {
        state.isMenuLoading = false;
      });

    // Locations
    builder
      .addCase(fetchStoreLocations.pending, (state) => {
        state.isLocationLoading = true;
      })
      .addCase(fetchStoreLocations.fulfilled, (state, action) => {
        state.isLocationLoading = false;
        state.storeLocations = action.payload.data;
        const selected = action.payload.data.locations.find(
          (loc) => String(loc.store_id) === action.payload.storeId,
        );
        state.selectedStore = selected;
        state.isLoyaltyActive = selected?.loyalty_on === "1" || false;
        state.isGiftcardActive = selected?.giftcard_on === "1" || false;
        state.loyaltyLevels = selected?.loyalty_levels || [];
      })
      .addCase(fetchStoreLocations.rejected, (state) => {
        state.isLocationLoading = false;
      });
  },
});

export const {
  setStoreId,
  setMenuId,
  setUtm,
  setStoreGateways,
  setGiftcardMenu,
  applyInflation,
} = storeSlice.actions;

export default storeSlice.reducer;

// ---- Selectors / helpers ----
export const selectHasAdInflation = (state: { store: StoreState }) =>
  state.store.utm === utmTag;

export const selectOrderTypes = createSelector(
  (state: RootState) => state.store.selectedStore,
  (selectedStore) =>
    Array.isArray(selectedStore?.order_type) ? selectedStore.order_type : [],
);

export const selectDefaultOrderType = createSelector(
  selectOrderTypes,
  (state: RootState) => state.orderOption.value.type_id,
  (orderTypes, typeId) => {
    if (typeId) return typeId;
    const available = new Set(orderTypes.map((t: OrderTypeModel) => t.id));
    if (available.has(OrderType.PICKUP)) return OrderType.PICKUP;
    if (available.has(OrderType.DELIVERY)) return OrderType.DELIVERY;
    if (available.has(OrderType.DINEIN)) return OrderType.DINEIN;
    return OrderType.PICKUP;
  },
);
